import { z } from 'zod'
import type { InviteRowFields } from './parse-batch-paste'

/**
 * Row Outcome plus the transient states of a send loop. `pending` rows are
 * queued for the next run; `notSent` means the run was stopped before the row
 * was dispatched.
 */
export type InviteRowStatus =
  | 'pending'
  | 'sending'
  | 'invited'
  | 'alreadyMember'
  | 'failed'
  | 'notSent'

export type InviteRow = InviteRowFields & {
  key: number
  status: InviteRowStatus
  /** Raw server error message, shown verbatim on Failed rows. */
  errorMessage?: string
}

export type RowField = keyof InviteRowFields

export type RowIssue = { field: RowField; message: string }

/**
 * Mirrors the single-invite field rules from invite-org-member.contract.ts.
 * The credentials refinements don't apply — a batch sends no credentials.
 */
const inviteRowFieldsSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email('Invalid email address').min(1, 'Email is required'),
})

/** Validation issues for one row, first issue per field. */
export function rowIssues(row: InviteRowFields): RowIssue[] {
  const result = inviteRowFieldsSchema.safeParse(row)
  if (result.success) return []

  const issues: RowIssue[] = []
  const seen = new Set<RowField>()
  for (const issue of result.error.issues) {
    const field = issue.path[0] as RowField
    if (seen.has(field)) continue
    seen.add(field)
    issues.push({ field, message: issue.message })
  }
  return issues
}

/**
 * Keys of rows whose email already appeared earlier in the batch
 * (trimmed, case-insensitive — the only place emails are lowercased).
 */
export function duplicateRowKeys(rows: readonly InviteRow[]): Set<number> {
  const seen = new Set<string>()
  const duplicates = new Set<number>()
  for (const row of rows) {
    const email = row.email.trim().toLowerCase()
    if (!email) continue
    if (seen.has(email)) {
      duplicates.add(row.key)
    } else {
      seen.add(email)
    }
  }
  return duplicates
}
